"use client";

import {
  NotificationPreference,
  updateComedianNotificationPreference,
} from "@/data/notification-preferences";
import { NotificationPreferencesEvents, trackEvent } from "@/analytics";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Database } from "@punchup/data/supabase.types";
import { comedianNotificationPreferenceKeys } from "./useComedianNotificationPreference";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { logException } from "@punchup/sentry";
import { toast } from "@/shadcn/ui/use-toast";

interface UpdatePreferenceParams {
  comedianId: string;
  preference: NotificationPreference;
  /** Previous preference for analytics tracking */
  previousPreference?: NotificationPreference;
}

interface MutationContext {
  previousPreference: NotificationPreference | undefined;
  comedianId: string;
}

/**
 * Hook to update a comedian's notification preference
 *
 * Features:
 * - Optimistic updates for immediate UI feedback
 * - Automatic rollback on error
 * - Cache invalidation on success
 * - Analytics tracking
 * - Toast notifications for errors
 *
 * @returns Mutation object with updatePreference function
 *
 * @example
 * ```tsx
 * const { mutate: updatePreference, isPending } = useUpdateComedianNotificationPreference();
 *
 * const handleChange = (newPreference: NotificationPreference) => {
 *   updatePreference({
 *     comedianId,
 *     preference: newPreference,
 *     previousPreference: currentPreference, // for analytics
 *   });
 * };
 * ```
 */
export function useUpdateComedianNotificationPreference() {
  const supabase = createClientComponentClient<Database>();
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, UpdatePreferenceParams, MutationContext>({
    mutationFn: async ({ comedianId, preference }) => {
      const success = await updateComedianNotificationPreference(
        supabase,
        comedianId,
        preference
      );
      if (!success) {
        throw new Error("Failed to update notification preference");
      }
      return success;
    },

    onMutate: async ({ comedianId, preference }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: comedianNotificationPreferenceKeys.detail(comedianId),
      });

      // Snapshot the previous value
      const previousPreference =
        queryClient.getQueryData<NotificationPreference>(
          comedianNotificationPreferenceKeys.detail(comedianId)
        );

      // Optimistically update to the new value
      queryClient.setQueryData(
        comedianNotificationPreferenceKeys.detail(comedianId),
        preference
      );

      // Return context with the previous value
      return { previousPreference, comedianId };
    },

    onError: (error, _variables, context) => {
      // Rollback to the previous value on error
      if (context) {
        queryClient.setQueryData(
          comedianNotificationPreferenceKeys.detail(context.comedianId),
          context.previousPreference
        );
      }

      toast({
        title: "Error",
        description: "Failed to update notification preferences",
        variant: "destructive",
      });

      console.error("Error updating notification preference:", error);
    },

    onSuccess: (
      _data,
      { comedianId, preference, previousPreference: paramsPrevPref },
      context
    ) => {
      queryClient.invalidateQueries({
        queryKey: comedianNotificationPreferenceKeys.detail(comedianId),
      });

      const resolvedPreviousPreference =
        paramsPrevPref ?? context?.previousPreference;

      if (!resolvedPreviousPreference) {
        logException(
          new Error("previousPreference was undefined in analytics tracking"),
          { extra: { comedianId, preference } }
        );
      }

      trackEvent(
        NotificationPreferencesEvents.CHANGED_COMEDIAN_NOTIFICATION_PREFERENCE,
        {
          comedian_id: comedianId,
          initial_preference: resolvedPreviousPreference ?? "unknown",
          new_preference: preference,
        }
      );
    },
  });
}
